export default function PrivacyPolicy() {
  return (
    <main className="terms-style px-10 md:px-20 lg:px-[15rem] xl:px-[25rem] py-10 dark:text-white">
      <h1 className="w-full flex justify-center text-lg mb-5">
        <b>NEWT: AIKA - PRIVACY POLICY</b>
      </h1>
      <span>Effective Date: February 15, 2022</span>
      <ol>
        <li>
          <h2>
            <b>1.</b>
            <u>
              <b>Introduction and Overview.</b>
            </u>
          </h2>
          <p>
            This Privacy Policy (this “Privacy Policy”) provides a comprehensive description of how
            Avara UI Labs Ltd. (“Avara UI Labs,” “we,” “our,” or “us”) collects, uses, and shares
            information about you in connection with the Newt: Aika Site, available at aika.gg (the
            “Site”), as well as your rights and choices regarding such information. These terms
            apply to the Site and any other online location that links to this Privacy Policy
            (collectively, the “Services”).
          </p>
          <p>
            By accessing or using the Site, you agree to our Terms of Service (the “Terms”) and
            understand that the Terms represent a binding agreement between you and us. By using the
            Services, you also agree to our collection, use, and disclosure practices, as well as
            any other activities described in this Privacy Policy. If you do not agree with the
            terms of this Privacy Policy, you should immediately discontinue the use of the Services
            and refrain from accessing the Site. If you have any questions or wish to exercise your
            rights and choices, please contact us at the email or portal address set forth in the
            “Contact Us” section below. If you are a California resident or a data subject in the
            European Economic Area or the United Kingdom, please see the additional disclosures at
            the end of this Privacy Policy.
          </p>
        </li>
        <li>
          <h2>
            <b>2.</b>
            <u>
              <b>Changes to this Privacy Policy.</b>
            </u>
          </h2>
          <p>
            We reserve the right to revise and reissue this Privacy Policy at any time. Any changes
            will be effective immediately upon our posting of the revised Privacy Policy. For the
            avoidance of doubt, your continued use of the Services indicates your consent to the
            revised Privacy Policy then posted.
          </p>
        </li>
        <li>
          <h2>
            <b>3.</b>
            <u>
              <b>Information Collection.</b>
            </u>
          </h2>
          <ol>
            <li>
              <h3>
                <span>A.</span>
                <b>Information You Provide</b>
              </h3>
              <p>We may collect the following information about you when you use the Services:</p>
              <ul>
                <li>
                  <b>Content,</b>
                  including content within any messages you send to us (such as feedback and
                  questions to customer support)
                </li>
              </ul>
              <p>
                You may choose to voluntarily provide other information to us that we have not
                solicited from you, and, in such instances, you are solely responsible for such
                information.
              </p>
            </li>
            <h3>
              <span>B.</span>
              <b>Information Collected Automatically.</b>
            </h3>
            <p>
              Although at this time we do not automatically collect information from you when you
              use the Avara UI Labs Services, it is possible that we may in the future. We would
              utilize this information to operate and ensure the security, reliability, and robust
              performance of our Services.
            </p>
            <p>
              We may also use tracking technologies to automatically collect information including
              the following:
            </p>
            <ul>
              <li>
                <b>Content,</b>
                including content within any messages you send to us (such as feedback and questions
                to customer support)
              </li>
            </ul>
            <p>
              You may choose to voluntarily provide other information to us that we have not
              solicited from you, and, in such instances, you are solely responsible for such
              information.
            </p>
            <ul>
              <li>
                <b>Log files,</b>
                which are files that record events that occur in connection with your use of the
                Avara UI Labs Services. Log files are created when you view content or otherwise
                interact with the Services.
              </li>
              <li>
                <b>Cookies,</b>
                which are small data files stored on your device that act as a unique tag to
                identify your browser. We will only use strictly necessary cookies in connection
                with the Site and Services. For the avoidance of doubt, the cookies that we include
                are essential for you to browse the Site and use its features, including accessing
                secure areas of the Site.
              </li>
            </ul>
            <p>
              For further information on how we use tracking technologies for analytics, and your
              rights and choices regarding them, please see the “Analytics” and “Your Rights and
              Choices” sections below.
            </p>
          </ol>
        </li>
        <li>
          <h2>
            <b>4.</b>
            <u>
              <b>Use of Information.</b>
            </u>
          </h2>
          <p>
            We do not anticipate using, but we may collect and use, information for business
            purposes in accordance with the practices described in this Privacy Policy. Our business
            purposes for collecting and using information include:
          </p>
          <ol className="list-decimal ml-10">
            <li>
              <p>
                Operating and managing the Services; performing services requested by you, such as
                responding to your comments, questions, and requests, and providing information
                support; sending you technical notices, updates, security alerts, information
                regarding changes to our policies, and support and administrative messages;
                detecting, preventing, and addressing fraud, breach of Terms, and threats, or harm;
                and compliance with legal and regulatory requirements.
              </p>
            </li>
            <li>
              <p>
                Protecting the security and integrity of the Services; improving the Services and
                other websites, apps, products and services; conducting promotions, such as
                hack-a-thons, including to verify your eligibility and deliver prizes in connection
                with your entries; and fulfilling any other business purpose, with notice to you and
                your consent.
              </p>
            </li>
          </ol>
          <p>
            Notwithstanding the above, we may use information that does not identify you (including
            information that has been aggregated or de-identified) for any purpose except as
            prohibited by applicable law. For information on your rights and choices with respect to
            how we use information about you, please see the “Your Rights and Choices” section
            below.
          </p>
        </li>
        <li>
          <h2>
            <b>5.</b>
            <u>
              <b>Sharing and Disclosure of Information.</b>
            </u>
          </h2>
          <p>
            We share or disclose information that we collect in accordance with the practices
            described in this Privacy Policy. The categories of parties with whom we may share
            information include:
          </p>
          <ul>
            <li>
              <b>Service Providers.</b>
              We share information with third-party service providers for business or commercial
              purposes, including fraud detection and prevention, security threat detection, payment
              processing, customer support, data analytics, information technology, storage, and
              transaction monitoring. Any information shared with such service providers is subject
              to the terms of this Privacy Policy. All service providers that we engage with are
              restricted to only utilizing the information on our behalf and in accordance with our
              instructions.
            </li>
            <li>
              <b>Professional Advisors.</b>
              We share information with our professional advisors for purposes of audits and
              compliance with our legal obligations.
            </li>
            <li>
              <b>Merger or Acquisition.</b>
              We share information in connection with, or during negotiations of, any proposed or
              actual merger, purchase, sale or any other type of acquisition or business combination
              of all or any portion of our assets, or transfer of all or a portion of our business
              to another business.
            </li>
            <li>
              <b>Security and Compelled Disclosure.</b>
              We share information to comply with the law or other legal process, and where
              required, in response to lawful requests by public authorities, including to meet
              national security or law enforcement requirements.
            </li>
            <li>
              <b>Facilitating Requests.</b>
              We may share information about you at your request or direction.
            </li>
            <li>
              <b>Consent.</b>
              We may share information about you with your consent.
            </li>
          </ul>
          <p>
            Notwithstanding the above, we may share information that does not identify you
            (including information that has been aggregated or de-identified) except as prohibited
            by applicable law. For information on your rights and choices regarding how we share
            information about you, please see the “Your Rights and Choices” section below.
          </p>
        </li>
        <li>
          <h2>
            <b>6.</b>
            <u>
              <b>Other Parties.</b>
            </u>
          </h2>
          <p>
            We may integrate technologies operated or controlled by other parties into parts of the
            Services. For example, the Services may include links that hyperlink to websites,
            platforms, and other services not operated or controlled by us.
          </p>
          <p>
            Please note that when you interact with other parties, including when you leave the
            Site, those parties may independently collect information about you and solicit
            information from you. The information collected and stored by those parties remains
            subject to their own policies and practices, including what information they share with
            us, your rights and choices on their services and devices, and whether they store
            information in the U.S. or elsewhere. We encourage you to familiarize yourself with and
            consult their privacy policies and terms of use.
          </p>
        </li>
        <li>
          <h2>
            <b>7.</b>
            <u>
              <b>Analytics.</b>
            </u>
          </h2>
          <p>We do not use analytics services for this Site.</p>
        </li>
        <li>
          <h2>
            <b>8.</b>
            <u>
              <b>Your Rights and Choices.</b>
            </u>
          </h2>
          <ul>
            <li>
              <b>Cookies.</b>
              We will only use strictly necessary cookies. These cookies are essential for you to
              browse the Site and use its features, including accessing secure areas of the Site.
            </li>
            <li>
              <b>Do Not Track.</b>
              Your browser settings may allow you to automatically transmit a “Do Not Track” signal
              to the online services you visit. Note, however, there is no industry consensus as to
              what site and app operators should do with regard to these signals. Accordingly,
              unless and until the law is interpreted to require us to do so, we do not monitor or
              take action with respect to “Do Not Track” signals. For more information on “Do Not
              Track,” visit
              <a href="http://www.allaboutdnt.com." target="_blank" rel="noreferrer">
                http://www.allaboutdnt.com.
              </a>
            </li>
          </ul>
          <p>
            Please be aware that if you disable or remove tracking technologies some parts of the
            Avara UI Labs Services may not function correctly.
          </p>
        </li>
        <li>
          <h2>
            <b>9.</b>
            <u>
              <b>Children.</b>
            </u>
          </h2>
          <p>
            The Services are intended for general audiences and are not directed at children. To use
            the Services, you must legally be able to enter into the Agreement. We do not knowingly
            collect personal information (as defined by the U.S. Children’s Privacy Protection Act,
            or “COPPA”) from children. If you are a parent or guardian and believe we have collected
            personal information in violation of COPPA, please contact us at
            <a href="mailto:privacy@avaralabsui.com"> privacy@avaralabsui.com </a>
            and we will remove the personal information in accordance with COPPA.
          </p>
        </li>
        <li>
          <h2>
            <b>10.</b>
            <u>
              <b>Data Security.</b>
            </u>
          </h2>
          <p>
            We implement and maintain reasonable administrative, physical, and technical security
            safeguards to help protect information about you from loss, theft, misuse, unauthorized
            access, disclosure, alteration, and destruction. Nevertheless, transmission via the
            internet is not completely secure and we cannot guarantee the security of information
            about you.
          </p>
        </li>
        <li>
          <h2>
            <b>11.</b>
            <u>
              <b>International Transfer.</b>
            </u>
          </h2>
          <p>
            Please be aware that information collected through the Services may be transferred to,
            processed, stored, and used in the European Economic Area, the United Kingdom, and other
            jurisdictions. Data protection laws in the EU and other jurisdictions may be different
            from those of your country of residence. Your use of the Services or provision of any
            information therefore constitutes your consent to the transfer to and from, processing,
            usage, sharing, and storage of information about you in the EU and other jurisdictions
            as set out in this Privacy Policy.
          </p>
        </li>
        <li>
          <h2>
            <b>12.</b>
            <u>
              <b>Contact Us.</b>
            </u>
          </h2>
          <p>
            If you have any questions or comments about this Privacy Policy, our data practices, or
            our compliance with applicable law, please contact us:
          </p>
          <p>
            By email: <a href="mailto:privacy@avaralabsui.com">privacy@avaralabsui.com </a>
          </p>
          <p>By mail:</p>
          <p className="flex flex-col">
            <span>Avara UI Labs Ltd. </span>
            <span>c/o Walkers Corporate Limited</span>
            <span>190 Elgin Avenue</span>
            <span>George Town, Grand Cayman</span>
            <span>KY1-9008, Cayman Islands</span>
          </p>
          <p>
            This Privacy Policy has been designed to be accessible to people with disabilities. If
            you experience any difficulties accessing the information here, please contact us at{' '}
            <a href="mailto:privacy@avaralabsui.com"> privacy@avaralabsui.com </a>
          </p>
        </li>
        <li>
          <h2>
            <b>13.</b>
            <u>
              <b>Additional Disclosures for California Residents.</b>
            </u>
          </h2>
          <p>
            These additional disclosures apply only to California residents. The California Consumer
            Privacy Act of 2018 (“CCPA”) provides additional rights to know, delete, and opt-out,
            and requires businesses collecting or disclosing personal information to provide notices
            and the means to exercise consumer rights.
          </p>
          <ol>
            <li>
              <h3>
                <span>A.</span>
                <b>Notice of Collection.</b>
              </h3>
              <p>
                We collect the following categories of personal information enumerated in the CCPA:
              </p>
              <ul>
                <li>
                  <b>Internet activity –</b>
                  including information on visitors to the Site and their interactions with the
                  Avara UI Labs Services.
                </li>
                <li>
                  <b>Geolocation data</b>
                  of Site visitors
                </li>
              </ul>
              <p>
                For further details on the information we may collect, including the sources from
                which we receive information, review the “Information Collection” section above. We
                may collect and use these categories of personal information for the business
                purposes described in the “Use of Information” section above, including to manage
                the Services.
              </p>
              <p>
                We do not “sell” personal information as defined under the CCPA. Please review the
                “Sharing and Disclosure of Information” section above for further details about the
                categories of parties with whom we share information.
              </p>
            </li>
            <li>
              <h3>
                <span>B.</span>
                <b>Right to Know and Delete. </b>
              </h3>
              <p>
                You have the right to know certain details about our data practices within the past
                twelve (12) months. In particular, you may request the following from us:
              </p>
              <ul>
                <li>The categories of personal information we have collected about you;</li>
                <li>
                  The categories of sources from which the personal information was collected;
                </li>
                <li>
                  The categories of personal information about you we disclosed for a business
                  purpose;
                </li>
                <li>
                  The categories of third parties to whom the personal information was disclosed for
                  a business purpose;
                </li>
                <li>
                  The business or commercial purpose for collecting or selling the personal
                  information; and
                </li>
                <li>The specific pieces of personal information we have collected about you.</li>
              </ul>
              <p>
                In addition, you have the right to delete the personal information we have collected
                from you.
              </p>
              <p>
                To exercise any of these rights, please submit a request by emailing us at{' '}
                <a href="mailto:privacy@avaralabsui.com"> privacy@avaralabsui.com</a>. In the
                request, please specify which right you are seeking to exercise and the scope of the
                request. We will confirm receipt of your request within ten (10) days. We may
                require specific information from you to help us verify your identity and process
                your request. If we are unable to verify your identity, we may deny your requests to
                know or delete.
              </p>
            </li>
            <li>
              <h3>
                <span>C.</span>
                <b>Authorized Agent.</b>
              </h3>
              <p>
                You may designate an authorized agent to submit requests on your behalf; however, we
                may require written proof of the agent’s permission to act on your behalf and verify
                your identity directly.
              </p>
            </li>
            <li>
              <h3>
                <span>D.</span>
                <b>Right of Non-Discrimination.</b>
              </h3>
              <p>
                You have a right of non-discrimination for the exercise of any of your privacy
                rights guaranteed by law, such as the right to access, delete, or opt-out of the
                sale of your personal information.
              </p>
            </li>
            <li>
              <h3>
                <span>E.</span>
                <b>Shine the Light.</b>
              </h3>
              <p>
                Customers who are residents of California may request (i) a list of the categories
                of personal information disclosed by us to third parties during the immediately
                preceding calendar year for those third parties’ own direct marketing purposes; and
                (ii) a list of the categories of third parties to whom we disclosed such
                information. To exercise a request, please write to us at the email or postal
                address set out in the “Contact Us” section above and specify that you are making a
                “California Shine the Light Request.” We may require additional information from you
                to allow us to verify your identity and are only required to respond to requests
                once per calendar year.
              </p>
            </li>
          </ol>
        </li>
        <li>
          <h2>
            <b>14.</b>
            <u>
              <b>
                Additional Disclosures for Data Subjects in the European Economic Area and the
                United Kingdom.
              </b>
            </u>
          </h2>
          <ol>
            <li>
              <h3>
                <span>A.</span>
                <b>Roles.</b>
              </h3>
              <p>
                The General Data Protection Regulations in the European Economic Area and the United
                Kingdom (“GDPR”) distinguish between organizations that process personal data for
                their own purposes (known as “controllers”) and organizations that process personal
                data on behalf of other organizations (known as “processors”). We act as a
                controller with respect to personal data collected as you interact with the
                Services.
              </p>
            </li>
            <li>
              <h3>
                <span>B.</span>
                <b>Lawful Basis for Processing.</b>
              </h3>
              <p>
                The GDPR requires a “lawful basis” for processing personal data. Our lawful bases
                include where: (a) you have given consent to the processing for one or more specific
                purposes, either to us or to our service providers or partners; (b) processing is
                necessary for the performance of a contract with you; (c) processing is necessary
                for compliance with a legal obligation; or (d) processing is necessary for the
                purposes of the legitimate interests pursued by us or a third party, and your
                interests and fundamental rights and freedoms do not override those interests. Where
                applicable, we will transfer your personal data to third parties subject to
                appropriate or suitable safeguards, such as standard contractual clauses.
              </p>
            </li>
            <li>
              <h3>
                <span>C.</span>
                <b>Your Data Subject Rights.</b>
              </h3>
              <p>
                If you are a user in the European Economic Area or the United Kingdom, you maintain
                certain rights under the GDPR. These rights include the right to (i) request access
                and obtain a copy of your personal data; (ii) request rectification or erasure of
                your personal data; (iii) object to or restrict the processing of your personal
                data; and (iv) request portability of your personal data. Additionally, if we have
                collected and processed your personal data with your consent, you have the right to
                withdraw your consent at any time.
              </p>
              <p>
                Notwithstanding the foregoing, we cannot edit or delete information that is stored
                on a particular blockchain. This information may include transaction data (i.e.,
                purchases, sales, and transfers) related to your blockchain wallet address and any
                NFTs held by your wallet address.
              </p>
              <p>
                To exercise any of these rights, please contact us via our email or postal address
                listed in the “Contact Us” section above and specify which right you are seeking to
                exercise. We will respond to your request within thirty (30) days. We may require
                specific information from you to help us confirm your identity and process your
                request. Please note that we retain information as necessary to fulfill the purpose
                for which it was collected and may continue to retain and use information even after
                a data subject request in accordance with our legitimate interests, including as
                necessary to comply with our legal obligations, resolve disputes, prevent fraud, and
                enforce our agreements.
              </p>
              <p>
                If you have any issues with our compliance, please contact us as set out in the
                “Contact Us” section above. You also reserve the right to lodge a complaint with the
                data protection regulator in your jurisdiction.
              </p>
            </li>
          </ol>
        </li>
      </ol>
    </main>
  );
}
